import React from "react";
import momentTZ from "moment-timezone";
import {
    GetGrafikPenjualanResponse,
    GetLaporanLabaRugiResponse,
    GetLaporanTotalResponse,
    ParamsGetLaporanLabaRugi,
    ParamsGetLaporanTotal,
    ParamsGetUkm,
    ParamsGrafikPenjualan,
} from "../../../constants/types";
import { apiUrl } from "../../../constants/common";
import { useAuthStore } from "../../../stores/authStore";
import useGrafikPenjualan from "../../../services/queries/useGrafikPenjualan";
import useLaporanLabaRugi from "../../../services/queries/useLaporanLabaRugi";
import useLaporanTotal from "../../../services/queries/useLaporanTotal";
import useNoWaAdmin from "../../../services/queries/useNoWaAdmin";
import useTimeAlt from "../../../services/queries/useTimeAlt";
import useUkm from "../../../services/queries/useUkm";
import useUser from "../../../services/queries/useUser";
import useSelectedUkm from "../../../services/queries/useSelectedUkm";

const paramsUkm: ParamsGetUkm = {
    size: 500,
    page: 1,
    search: "",
};

type UseLaporanPageDataParams = {
    tanggalAwal: string;
    tanggalAkhir: string;
};

function handleConvertSatuan(value: string | number) {
    const numericValue = Number(value);

    if (numericValue >= 1000000000) {
        return `${numericValue / 1000000000}m`;
    }

    if (numericValue >= 1000000) {
        return `${numericValue / 1000000}jt`;
    }

    if (numericValue >= 1000) {
        return `${numericValue / 1000}rb`;
    }

    return String(value);
}

export default function useLaporanPageData({
    tanggalAwal,
    tanggalAkhir,
}: UseLaporanPageDataParams) {
    const { data: noWaAdmin } = useNoWaAdmin();
    const { data: user, status: loadingUser } = useUser();
    const ukmIdUser = useAuthStore((s) => s.ukmIdUser);
    const selectedUkmId = ukmIdUser && ukmIdUser[0];
    const effectiveTanggalAkhir = tanggalAkhir || tanggalAwal;
    const timezone = momentTZ.tz.guess();

    const paramsGrafikPenjualan = React.useMemo<ParamsGrafikPenjualan>(
        () => ({
            ukmId: selectedUkmId,
            tanggalAwal,
            tanggalAkhir: effectiveTanggalAkhir,
        }),
        [selectedUkmId, tanggalAwal, effectiveTanggalAkhir],
    );

    const paramsLaporanTotal = React.useMemo<ParamsGetLaporanTotal>(
        () => ({
            ukmId: selectedUkmId,
            tanggalAwal,
            tanggalAkhir: effectiveTanggalAkhir,
        }),
        [selectedUkmId, tanggalAwal, effectiveTanggalAkhir],
    );

    const paramsLaporanLabaRugi = React.useMemo<ParamsGetLaporanLabaRugi>(
        () => ({
            ukmId: selectedUkmId,
            tglawal: tanggalAwal,
            tglakhir: effectiveTanggalAkhir,
        }),
        [selectedUkmId, tanggalAwal, effectiveTanggalAkhir],
    );

    const { data: ukm } = useUkm(paramsUkm);
    const { data: time } = useTimeAlt();
    const {
        data: grafikPenjualan,
        isLoading: isLoadingGrafikPenjualan,
        isError: isErrorGrafik,
    } = useGrafikPenjualan(paramsGrafikPenjualan);
    const {
        data: laporanTotal,
        isLoading: isLoadingLaporanTotal,
        isError: isErrorLaporanTotal,
    } = useLaporanTotal(paramsLaporanTotal);
    const {
        data: laporanLabaRugi,
        isLoading: isLoadingLaporanLabaRugi,
        isError: isErrorLaporanLabaRugi,
    } = useLaporanLabaRugi(paramsLaporanLabaRugi);

    const { ukmData, isUkmTidakAktif } = useSelectedUkm(user, ukm, time);

    const hasNoAccess =
        user?.aksesLaporan === 0 || user?.aksesLaporan === null;

    const chartOptions = React.useMemo(
        () => ({
            plugins: { legend: { display: false } },
            maintainAspectRatio: false,
            responsive: true,
            interaction: {
                mode: "index" as const,
                intersect: false,
            },
            stacked: false,
            locale: "id-ID",
            scales: {
                x: {
                    display: true,
                    grid: {
                        drawOnChartArea: false,
                    },
                },
                y: {
                    display: true,
                    position: "left" as const,
                    ticks: {
                        callback: function (value: string | number) {
                            return handleConvertSatuan(value);
                        },
                    },
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: "Total Pendapatan",
                    },
                },
                y1: {
                    display: true,
                    position: "right" as const,
                    grid: {
                        drawOnChartArea: false,
                    },
                    gridLines: {
                        drawBorder: false,
                    },
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: "Jumlah Transaksi",
                    },
                },
            },
        }),
        [],
    );

    const chartData = React.useMemo(
        () => ({
            labels:
                (
                    grafikPenjualan as
                        | undefined
                        | GetGrafikPenjualanResponse["data"]
                )?.labels ?? [],
            datasets: [
                {
                    label: "Total Pendapatan",
                    data: grafikPenjualan?.data.totalPendapatan ?? [],
                    borderColor: "#45A779",
                    backgroundColor: "#45A779",
                    yAxisID: "y",
                    tension: 0.5,
                    pointRadius: 0,
                },
                {
                    label: "Total Transaksi",
                    data: grafikPenjualan?.data.totalTransaksi ?? [],
                    borderColor: "#e8d43a",
                    backgroundColor: "#e8d43a",
                    yAxisID: "y1",
                    tension: 0.5,
                    pointRadius: 0,
                },
            ],
        }),
        [grafikPenjualan],
    );

    const apiExportLaporan = `${apiUrl}api/downloadlaporan?ukmId=${
        selectedUkmId ?? ""
    }&tanggalAwal=${paramsLaporanTotal.tanggalAwal}&tanggalAkhir=${
        paramsLaporanTotal.tanggalAkhir
    }&timezone=${timezone}&isInvoice=1&isProduk=1&isMutasi=1`;

    return {
        apiExportLaporan,
        chartData,
        chartOptions,
        grafikPenjualan,
        hasNoAccess,
        isError:
            isErrorGrafik || isErrorLaporanLabaRugi || isErrorLaporanTotal,
        isLoadingGrafikPenjualan,
        isLoadingLaporanLabaRugi,
        isLoadingLaporanTotal,
        isLoadingUser: loadingUser === "loading",
        isUkmTidakAktif,
        laporanLabaRugi:
            laporanLabaRugi as
                | undefined
                | GetLaporanLabaRugiResponse["data"],
        laporanTotal:
            laporanTotal as undefined | GetLaporanTotalResponse["data"],
        noWaAdmin,
        paramsLaporanTotal,
        selectedUkmId,
        timezone,
        ukmData,
        user,
    };
}
